import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import type { Backtest, Script } from "@/api/types";
import { useApi } from "@/context/ApiContext";
import { ScriptEditor } from "@/components/ScriptEditor";
import { Pill } from "@/components/ui/kit";
import { parseScriptRef, resolveStrategySource } from "@/lib/script-ref";

/** The script a live strategy runs: pinned to one revision, shown read-only. */
export function LiveScriptPanel({ backtest, editorHeight = 420 }: { backtest: Backtest; editorHeight?: number | string }) {
  const api = useApi(["scripts"]);
  const pinned = String(backtest.strategy_config?.source || "");
  const ref = parseScriptRef(pinned);
  const script: Script | undefined = ref ? (api.scripts || []).find((s: Script) => s.id === ref.id) : undefined;
  const [source, setSource] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSource(null);
    resolveStrategySource(api.commands, pinned, api.scripts || [])
      .then((text) => !cancelled && setSource(text))
      .catch(() => !cancelled && setSource(""));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned, api.ready]);

  const name = script?.name?.trim() || source?.match(/(?:strategy|indicator)\s*\(\s*["']([^"']+)["']/)?.[1] || "Pine Script";
  const latest = script?.rev_no ?? 0;
  const newer = ref && ref.rev >= 1 && latest > ref.rev;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-[13px] font-semibold text-[var(--text-primary)]">{name}</b>
        {ref && ref.rev >= 1 && <Pill tone="mute" dot={false}>{`Revision ${ref.rev}`}</Pill>}
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)]">
          <Lock size={12} /> Read only
        </span>
      </div>
      <p className="m-0 text-xs text-[var(--text-muted)]">
        A live strategy keeps the script it started with.
        {newer ? ` The script is now at revision ${latest}; this strategy still runs revision ${ref.rev}.` : ""} To trade a changed
        script, go live with its backtest again.
      </p>
      {source === null ? (
        <div className="py-10 text-center text-[13px] text-[var(--text-muted)]">Loading the script…</div>
      ) : source ? (
        <div className="overflow-hidden rounded-[12px] border border-[var(--border)]">
          <ScriptEditor value={source} readOnly height={editorHeight} />
        </div>
      ) : (
        <div className="py-10 text-center text-[13px] text-[var(--text-muted)]">The script for this strategy isn't available.</div>
      )}
    </div>
  );
}
